import { useEffect, useState } from 'react'
import { useStoredState } from '../hooks/useStoredState'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { consultasStockApi, stockBajoApi } from '../api/consultasStock'
import { RespuestaDeposito } from '../components/StockDeposito'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import { colors, text, space, sx } from '../design/tokens'
import { fmtFecha, fmtFechaHora } from '../utils/fecha'
import { useAutoRefresh } from '../hooks/useAutoRefresh'

// STOCK → Consultas a depósito: todo lo que Ventas le preguntó a depósito,
// quién respondió y qué. Lo abierto primero; "Todas" trae el historial.
// Segunda solapa (Martin, 24 oct 2026): los faltantes / stock bajo, que se
// cargan también desde la compu igual que en el celular (/m/consultas-stock).
const ESTADOS = {
  pendiente: { t: 'Pendiente', bg: colors.warnBg, c: colors.warn },
  respondida_parcial: { t: 'Respondida parcial', bg: colors.warnBg, c: colors.warn },
  respondida: { t: 'Respondida', bg: colors.okBg, c: colors.ok },
  vencida: { t: 'Vencida', bg: colors.errBg, c: colors.err },
  cancelada: { t: 'Cancelada', bg: colors.bgSoft, c: colors.textSec },
}

export default function ConsultasStockPage() {
  const [params, setParams] = useSearchParams()
  const vista = params.get('vista') === 'faltantes' ? 'faltantes' : 'consultas'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>
      <div>
        <h1 style={{ margin: 0 }}>Consultas a depósito</h1>
        <p style={{ margin: '4px 0 0', fontSize: text.sec, color: colors.textSec }}>
          {vista === 'faltantes'
            ? 'Cada aviso sale con su número en los 3 informes de reposición de los lunes siguientes. No marca el artículo sin stock.'
            : 'Ventas las pide desde la NV con “🏬 Pedir stock a depósito”; depósito responde desde el celular.'}
        </p>
      </div>
      <div style={{ display: 'flex', gap: space.sm }}>
        {[['consultas', 'Consultas de Ventas'], ['faltantes', '📉 Faltantes / stock bajo']].map(([v, t]) => (
          <button key={v} onClick={() => setParams(v === 'faltantes' ? { vista: v } : {}, { replace: true })} style={{ ...sx.pill, ...(vista === v ? sx.pillActive : {}) }}>{t}</button>
        ))}
      </div>
      {vista === 'faltantes' ? <Faltantes /> : <Consultas />}
    </div>
  )
}

function Consultas() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [filtro, setFiltro] = useStoredState('lgr.filtros.consultasStock', 'abiertas')
  const [consultas, setConsultas] = useState(null)
  const [error, setError] = useState(null)

  const cargar = () => consultasStockApi.listar({ estado: filtro })
    .then(({ data }) => { setConsultas(data.consultas); setError(null) })
    .catch(() => setError('No se pudieron cargar las consultas.'))

  useEffect(() => { cargar() }, [filtro]) // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(cargar, 30000)

  const t = q.trim().toLowerCase()
  const filtradas = (consultas || []).filter(c => !t || [c.vendedor, c.nv_numero, String(c.id), ...c.items.flatMap(it => [it.descripcion, it.codigo_alfa])]
    .some(v => (v || '').toLowerCase().includes(t)))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.lg, flexWrap: 'wrap' }}>
        <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar producto, SKU, vendedor, NV o Nº" style={{ ...sx.input, width: 320, maxWidth: '100%' }} />
        <div style={{ display: 'flex', gap: space.sm }}>
          {[['abiertas', 'Abiertas'], ['todas', 'Todas']].map(([v, t]) => (
            <button key={v} onClick={() => setFiltro(v)} style={{ ...sx.pill, ...(filtro === v ? sx.pillActive : {}) }}>{t}</button>
          ))}
        </div>
      </div>

      {error && <div style={{ color: colors.err, fontSize: text.body }}>{error}</div>}
      {!consultas && !error && <div style={{ color: colors.textMeta, fontSize: text.body }}>Cargando…</div>}
      {consultas && filtradas.length === 0 && <div style={{ ...sx.card, color: colors.textMeta, fontSize: text.body }}>{t ? 'Ninguna consulta coincide con la búsqueda.' : filtro === 'abiertas' ? 'No hay consultas abiertas.' : 'Todavía no hay consultas.'}</div>}

      {consultas && filtradas.length > 0 && (
        <div style={{ ...sx.card, padding: 0, overflowX: 'auto' }}>
          <table style={sx.table}>
            <thead>
              <tr>
                {['#', 'Fecha', 'Vendedor', 'NV', 'Productos y respuesta', 'Estado'].map(h => <th key={h} style={sx.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtradas.map(c => {
                const e = ESTADOS[c.estado] || ESTADOS.pendiente
                return (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${colors.borderSoft}`, cursor: c.nota_venta_id ? 'pointer' : 'default' }}
                    title={c.nota_venta_id ? 'Abrir la NV' : undefined}
                    onClick={() => c.nota_venta_id && navigate(`/notas-venta/${c.nota_venta_id}`)}>
                    <td style={{ ...sx.td, ...sx.mono, padding: `${space.lg}px ${space.xl}px`, verticalAlign: 'top' }}>{c.id}</td>
                    <td style={{ ...sx.td, ...sx.mono, verticalAlign: 'top', paddingTop: space.lg, whiteSpace: 'nowrap' }}>{fmtFechaHora(c.created_at)}</td>
                    <td style={{ ...sx.td, verticalAlign: 'top', paddingTop: space.lg }}>{c.vendedor}</td>
                    <td style={{ ...sx.td, ...sx.mono, verticalAlign: 'top', paddingTop: space.lg, whiteSpace: 'nowrap' }}>{c.nv_numero || '—'}</td>
                    <td style={{ ...sx.td, padding: `${space.md}px ${space.xl}px` }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
                        {c.items.map(it => (
                          <div key={it.id} style={{ display: 'grid', gridTemplateColumns: '170px minmax(0, 1fr)', gap: space.lg, alignItems: 'start' }}>
                            <RespuestaDeposito item={it} />
                            <div style={{ minWidth: 0 }}>
                              <div>{it.descripcion}</div>
                              <div style={{ fontSize: text.sec, color: colors.textMeta }}><span style={sx.mono}>{it.codigo_alfa}</span>{it.cantidad !== null && <> · pide <span style={sx.mono}>{it.cantidad}</span></>}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td style={{ ...sx.td, verticalAlign: 'top', paddingTop: space.lg }}>
                      <span style={{ ...sx.badge, background: e.bg, color: e.c, whiteSpace: 'nowrap' }}>{e.t}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Faltantes / stock bajo desde la compu: el mismo aviso que depósito hace con
// el celular — se elige el producto con el selector único de artículos.
function Faltantes() {
  const [selector, setSelector] = useState(false)
  const [buscando, setBuscando] = useState(false)
  const [elegido, setElegido] = useState(null)
  const [hecho, setHecho] = useState(null)
  const [avisos, setAvisos] = useState(null)
  const [q, setQ] = useState('')
  const [error, setError] = useState('')

  const cargar = () => stockBajoApi.listar({ limite: 300 })
    .then(({ data }) => setAvisos(data.avisos))
    .catch(() => setAvisos([]))
  useEffect(() => { cargar() }, [])

  const elegir = async (a) => {
    setSelector(false); setHecho(null); setElegido(null); setError(''); setBuscando(true)
    try {
      const { data } = await stockBajoApi.buscar(a.codigo_alfa)
      const art = data.articulos.find(x => x.id === a.id) || data.articulos[0]
      if (art) setElegido(art)
      else setError(`No se encontró ${a.codigo_alfa}.`)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo buscar el producto.')
    } finally { setBuscando(false) }
  }

  const t = q.trim().toLowerCase()
  const filtrados = (avisos || []).filter(a => !t || [a.descripcion, a.codigo_alfa, a.usuario, a.nota, String(a.numero)]
    .some(v => (v || '').toLowerCase().includes(t)))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.lg, flexWrap: 'wrap' }}>
        <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar en los avisos: producto, SKU, quién avisó, Nº" style={{ ...sx.input, width: 320, maxWidth: '100%' }} />
        <button onClick={() => setSelector(true)} style={sx.btnPrimary}>＋ Avisar faltante</button>
      </div>

      {error && <div style={{ color: colors.err, fontSize: text.body }}>{error}</div>}
      {buscando && <div style={{ color: colors.textMeta, fontSize: text.body }}>Consultando el stock en Presea…</div>}
      {elegido && !hecho && (
        <FormAviso articulo={elegido} onCancelar={() => setElegido(null)} onHecho={(av) => { setHecho(av); setElegido(null); cargar() }} />
      )}
      {hecho && (
        <div style={{ ...sx.card, borderColor: colors.ok, background: colors.okSoft, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.lg, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 600, color: colors.ok }}>✓ Aviso Nº {hecho.numero} registrado — {hecho.descripcion}</div>
            <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: 2 }}>
              Sale en el informe de reposición de los lunes {hecho.lunes.map(fmtFecha).map(f => f.slice(0, 5)).join(', ')}.
            </div>
          </div>
          <button onClick={() => setSelector(true)} style={sx.btnSecondary}>＋ Avisar otro</button>
        </div>
      )}

      {!avisos && <div style={{ color: colors.textMeta, fontSize: text.body }}>Cargando…</div>}
      {avisos && filtrados.length === 0 && <div style={{ ...sx.card, color: colors.textMeta, fontSize: text.body }}>{t ? 'Ningún aviso coincide con la búsqueda.' : 'Todavía no hay avisos de stock bajo.'}</div>}
      {avisos && filtrados.length > 0 && (
        <div style={{ ...sx.card, padding: 0, overflowX: 'auto' }}>
          <table style={sx.table}>
            <thead>
              <tr>{['Nº', 'Fecha', 'Avisó', 'SKU', 'Producto', 'Quedan', 'Stock Presea al avisar', 'Nota'].map(h => <th key={h} style={sx.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtrados.map(a => (
                <tr key={a.numero} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                  <td style={{ ...sx.td, ...sx.mono }}>{a.numero}</td>
                  <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>{fmtFechaHora(a.created_at)}</td>
                  <td style={sx.td}>{a.usuario || '—'}</td>
                  <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>{a.codigo_alfa}</td>
                  <td style={sx.td}>{a.descripcion}</td>
                  <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{a.quedan ?? '—'}</td>
                  <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{a.stock_presea ?? '—'}</td>
                  <td style={{ ...sx.td, color: colors.textSec }}>{a.nota || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selector && (
        <SelectorArticulosModal modo="simple" titulo="Avisar faltante / stock bajo" conFoto onSelect={elegir} onClose={() => setSelector(false)} />
      )}
    </div>
  )
}

function FormAviso({ articulo, onHecho, onCancelar }) {
  const [quedan, setQuedan] = useState('')
  const [nota, setNota] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const sp = articulo.stock_presea

  const avisar = async () => {
    setEnviando(true); setError('')
    try {
      const { data } = await stockBajoApi.avisar({
        articulo_id: articulo.id,
        quedan: quedan.trim() === '' ? null : Number(quedan.replace(',', '.')),
        nota: nota.trim() || null,
        stock_presea: sp || null,
      })
      onHecho(data.aviso)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo registrar el aviso.')
    } finally { setEnviando(false) }
  }

  return (
    <div style={{ ...sx.card, display: 'flex', flexDirection: 'column', gap: space.lg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: space.lg, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600 }}>{articulo.descripcion}</div>
          <div style={{ fontSize: text.sec, color: colors.textMeta }}><span style={sx.mono}>{articulo.codigo_alfa}</span>{articulo.ean ? <> · <span style={sx.mono}>{articulo.ean}</span></> : ''}</div>
          {articulo.ultimo_aviso && (
            <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: 4 }}>
              Último aviso: Nº {articulo.ultimo_aviso.numero} · {fmtFechaHora(articulo.ultimo_aviso.created_at)}{articulo.ultimo_aviso.usuario ? ` · ${articulo.ultimo_aviso.usuario}` : ''}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stock Presea</div>
          <div style={{ ...sx.mono, fontSize: 20, fontWeight: 700 }}>{sp ? sp.disponible : '—'}</div>
          {!sp && <div style={{ fontSize: text.sec, color: colors.textMeta }}>Presea no respondió</div>}
          {articulo.sin_stock && <span style={{ ...sx.badge, background: colors.errBg, color: colors.err }}>marcado sin stock</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: text.sec, color: colors.textSec }}>
          ¿Cuántas quedan? (opcional)
          <input value={quedan} onChange={e => setQuedan(e.target.value)} inputMode="decimal" style={{ ...sx.input, width: 110, minWidth: 0 }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: text.sec, color: colors.textSec, flex: 1, minWidth: 220 }}>
          Nota (opcional)
          <input value={nota} onChange={e => setNota(e.target.value)} maxLength={500} placeholder="ej. queda una caja abierta" style={sx.input} />
        </label>
        <div style={{ display: 'flex', gap: space.sm }}>
          <button onClick={onCancelar} style={sx.btnSecondary}>Cancelar</button>
          <button onClick={avisar} disabled={enviando} style={sx.btnPrimary}>{enviando ? 'Registrando…' : '📉 Avisar stock bajo'}</button>
        </div>
      </div>
      {error && <div style={{ color: colors.err, fontSize: text.body }}>{error}</div>}
    </div>
  )
}
